import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut, LayoutDashboard, FolderOpen, Briefcase,
  Users, UserCircle, Settings, Plus, Edit2, Trash2,
  X, Save, AlertTriangle, CheckCircle, Eye, EyeOff
} from "lucide-react";

const API_URL = "http://localhost:3001/api";

// ─── Toast ─────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -20 }}
    className={`fixed top-5 right-5 z-[100] flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-white text-sm font-medium ${
      type === "success" ? "bg-green-600" : "bg-red-600"
    }`}
  >
    {type === "success" ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
    {message}
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">
      <X size={14} />
    </button>
  </motion.div>
);

// ─── Confirm Modal ───────────────────────────────────────────────────────
const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
  >
    <motion.div
      initial={{ scale: 0.9 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.9 }}
      className="bg-white rounded-2xl p-7 max-w-sm w-full shadow-xl"
    >
      <div className="flex items-center gap-4 mb-5">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
          <AlertTriangle size={22} className="text-red-600" />
        </div>
        <p className="text-gray-800 font-medium">{message}</p>
      </div>
      <div className="flex gap-3">
        <button
          onClick={onConfirm}
          className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors"
        >
          Delete
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// ─── Generic Table ───────────────────────────────────────────────────────
const DataTable = ({ items, columns, onEdit, onDelete }) => (
  <div className="overflow-x-auto rounded-xl border border-gray-200">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              className="text-left px-5 py-3 font-semibold text-gray-600 whitespace-nowrap"
            >
              {col.label}
            </th>
          ))}
          <th className="text-left px-5 py-3 font-semibold text-gray-600">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.map((item, i) => (
          <tr key={item.id || i} className="hover:bg-gray-50 transition-colors">
            {columns.map((col) => (
              <td key={col.key} className="px-5 py-3.5 text-gray-700">
                {col.render ? col.render(item[col.key], item) : String(item[col.key] ?? "")}
              </td>
            ))}
            <td className="px-5 py-3.5">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEdit(item)}
                  className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg transition-colors"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => onDelete(item)}
                  className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {items.length === 0 && (
      <div className="text-center py-10 text-gray-400">No items yet.</div>
    )}
  </div>
);

// ─── Projects Section ────────────────────────────────────────────────────
const ProjectsSection = ({ onToast }) => {
  const [data, setData] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/projects`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  const save = async () => {
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API_URL}/projects/${editing.id}` : `${API_URL}/projects`;
    const payload = {
      ...form,
      id: editing ? editing.id : Date.now(),
      techStack: form.techStack
        ? typeof form.techStack === "string"
          ? form.techStack.split(",").map((s) => s.trim())
          : form.techStack
        : [],
    };
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const updated = await res.json();
      setData(updated);
      setEditing(null);
      setShowForm(false);
      setForm({});
      onToast("Saved successfully!", "success");
    } else {
      onToast("Save failed — is the server running?", "error");
    }
  };

  const remove = async (item) => {
    const res = await fetch(`${API_URL}/projects/${item.id}`, { method: "DELETE" });
    if (res.ok) {
      const updated = await res.json();
      setData(updated);
      setDeleteTarget(null);
      onToast("Deleted successfully!", "success");
    }
  };

  const startEdit = (item) => {
    setEditing(item);
    setForm({ ...item, techStack: item.techStack?.join(", ") });
    setShowForm(true);
  };

  const startAdd = () => {
    setEditing(null);
    setForm({ title: "", titleAr: "", category: "", description: "", descriptionAr: "", image: "", techStack: "", link: "#" });
    setShowForm(true);
  };

  const columns = [
    { key: "title", label: "Title" },
    { key: "category", label: "Category" },
    {
      key: "techStack",
      label: "Tech",
      render: (v) => (
        <div className="flex flex-wrap gap-1">
          {(Array.isArray(v) ? v : []).slice(0, 3).map((t) => (
            <span key={t} className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">{t}</span>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900">Projects</h2>
        <button
          onClick={startAdd}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} /> Add Project
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">{editing ? "Edit Project" : "New Project"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ["title", "Title (EN)"],
              ["titleAr", "Title (AR)"],
              ["category", "Category (Web/Mobile/AI/SaaS/ERP)"],
              ["image", "Image URL"],
              ["techStack", "Tech Stack (comma-separated)"],
              ["link", "Project Link"],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="text-xs text-gray-500 block mb-1">{label}</label>
                <input
                  value={form[key] || ""}
                  onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 block mb-1">Description (EN)</label>
              <textarea
                rows={2}
                value={form.description || ""}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 block mb-1">Description (AR)</label>
              <textarea
                rows={2}
                value={form.descriptionAr || ""}
                onChange={(e) => setForm((p) => ({ ...p, descriptionAr: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={save}
              className="flex items-center gap-2 bg-accent text-primary px-5 py-2 rounded-xl text-sm font-semibold hover:bg-accent/90 transition-colors"
            >
              <Save size={14} /> Save
            </button>
            <button
              onClick={() => { setShowForm(false); setEditing(null); setForm({}); }}
              className="flex items-center gap-2 bg-gray-200 text-gray-700 px-5 py-2 rounded-xl text-sm font-semibold"
            >
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      <DataTable
        items={data}
        columns={columns}
        onEdit={startEdit}
        onDelete={(item) => setDeleteTarget(item)}
      />

      <AnimatePresence>
        {deleteTarget && (
          <ConfirmModal
            message={`Delete "${deleteTarget.title}"?`}
            onConfirm={() => remove(deleteTarget)}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Jobs Section ─────────────────────────────────────────────────────────
const JobsSection = ({ onToast }) => {
  const [data, setData] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/jobs`).then((r) => r.json()).then(setData).catch(() => {});
  }, []);

  const save = async () => {
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API_URL}/jobs/${editing.id}` : `${API_URL}/jobs`;
    const payload = { ...form, id: editing ? editing.id : Date.now(),
      requirements: typeof form.requirements === "string" ? form.requirements.split(",").map(s=>s.trim()) : (form.requirements||[]),
      requirementsAr: typeof form.requirementsAr === "string" ? form.requirementsAr.split(",").map(s=>s.trim()) : (form.requirementsAr||[]),
    };
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (res.ok) { setData(await res.json()); setShowForm(false); setEditing(null); setForm({}); onToast("Saved!", "success"); }
    else onToast("Save failed", "error");
  };

  const remove = async (item) => {
    const res = await fetch(`${API_URL}/jobs/${item.id}`, { method: "DELETE" });
    if (res.ok) { setData(await res.json()); setDeleteTarget(null); onToast("Deleted!", "success"); }
  };

  const columns = [
    { key: "title", label: "Title" },
    { key: "department", label: "Department" },
    { key: "location", label: "Location" },
    { key: "type", label: "Type" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900">Jobs</h2>
        <button onClick={() => { setEditing(null); setForm({ title:"",titleAr:"",department:"",departmentAr:"",location:"",locationAr:"",type:"Full-time",typeAr:"دوام كامل",description:"",descriptionAr:"",requirements:"",requirementsAr:"" }); setShowForm(true); }} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-xl text-sm font-semibold">
          <Plus size={16} /> Add Job
        </button>
      </div>
      {showForm && (
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">{editing ? "Edit Job" : "New Job"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[["title","Title (EN)"],["titleAr","Title (AR)"],["department","Department (EN)"],["departmentAr","Department (AR)"],["location","Location (EN)"],["locationAr","Location (AR)"],["type","Type (EN)"],["typeAr","Type (AR)"],["requirements","Requirements (EN, comma-sep)"],["requirementsAr","Requirements (AR, comma-sep)"]].map(([k,l])=>(
              <div key={k}>
                <label className="text-xs text-gray-500 block mb-1">{l}</label>
                <input value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"/>
              </div>
            ))}
            {[["description","Description (EN)"],["descriptionAr","Description (AR)"]].map(([k,l])=>(
              <div key={k} className="sm:col-span-2">
                <label className="text-xs text-gray-500 block mb-1">{l}</label>
                <textarea rows={2} value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"/>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={save} className="flex items-center gap-2 bg-accent text-primary px-5 py-2 rounded-xl text-sm font-semibold"><Save size={14}/> Save</button>
            <button onClick={()=>{setShowForm(false);setEditing(null);setForm({});}} className="flex items-center gap-2 bg-gray-200 text-gray-700 px-5 py-2 rounded-xl text-sm font-semibold"><X size={14}/> Cancel</button>
          </div>
        </div>
      )}
      <DataTable items={data} columns={columns} onEdit={(item)=>{setEditing(item);setForm({...item,requirements:item.requirements?.join(", "),requirementsAr:item.requirementsAr?.join(", ")});setShowForm(true);}} onDelete={setDeleteTarget}/>
      <AnimatePresence>{deleteTarget&&<ConfirmModal message={`Delete "${deleteTarget.title}"?`} onConfirm={()=>remove(deleteTarget)} onCancel={()=>setDeleteTarget(null)}/>}</AnimatePresence>
    </div>
  );
};

// ─── Clients Section ──────────────────────────────────────────────────────
const ClientsSection = ({ onToast }) => {
  const [data, setData] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/clients`).then(r=>r.json()).then(setData).catch(()=>{});
  }, []);

  const save = async () => {
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API_URL}/clients/${editing.id}` : `${API_URL}/clients`;
    const res = await fetch(url, { method, headers: {"Content-Type":"application/json"}, body: JSON.stringify({...form, id: editing?editing.id:Date.now(), rating: Number(form.rating)||5}) });
    if (res.ok) { setData(await res.json()); setShowForm(false); setEditing(null); setForm({}); onToast("Saved!", "success"); }
    else onToast("Save failed","error");
  };

  const remove = async (item) => {
    const res = await fetch(`${API_URL}/clients/${item.id}`, { method:"DELETE" });
    if(res.ok){ setData(await res.json()); setDeleteTarget(null); onToast("Deleted!","success"); }
  };

  const columns=[{key:"name",label:"Name"},{key:"industry",label:"Industry"},{key:"rating",label:"Rating",render:v=>"★".repeat(v)}];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900">Clients</h2>
        <button onClick={()=>{setEditing(null);setForm({name:"",industry:"",industryAr:"",logo:"",testimonial:"",testimonialAr:"",rating:5});setShowForm(true);}} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-xl text-sm font-semibold"><Plus size={16}/> Add Client</button>
      </div>
      {showForm && (
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">{editing?"Edit Client":"New Client"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[["name","Name"],["industry","Industry (EN)"],["industryAr","Industry (AR)"],["logo","Logo URL"],["rating","Rating (1-5)"]].map(([k,l])=>(
              <div key={k}><label className="text-xs text-gray-500 block mb-1">{l}</label><input type={k==="rating"?"number":"text"} min="1" max="5" value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"/></div>
            ))}
            {[["testimonial","Testimonial (EN)"],["testimonialAr","Testimonial (AR)"]].map(([k,l])=>(
              <div key={k} className="sm:col-span-2"><label className="text-xs text-gray-500 block mb-1">{l}</label><textarea rows={2} value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"/></div>
            ))}
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={save} className="flex items-center gap-2 bg-accent text-primary px-5 py-2 rounded-xl text-sm font-semibold"><Save size={14}/> Save</button>
            <button onClick={()=>{setShowForm(false);setEditing(null);setForm({});}} className="flex items-center gap-2 bg-gray-200 text-gray-700 px-5 py-2 rounded-xl text-sm font-semibold"><X size={14}/> Cancel</button>
          </div>
        </div>
      )}
      <DataTable items={data} columns={columns} onEdit={(item)=>{setEditing(item);setForm(item);setShowForm(true);}} onDelete={setDeleteTarget}/>
      <AnimatePresence>{deleteTarget&&<ConfirmModal message={`Delete "${deleteTarget.name}"?`} onConfirm={()=>remove(deleteTarget)} onCancel={()=>setDeleteTarget(null)}/>}</AnimatePresence>
    </div>
  );
};

// ─── Team Section ─────────────────────────────────────────────────────────
const TeamSection = ({ onToast }) => {
  const [data, setData] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/team`).then(r=>r.json()).then(setData).catch(()=>{});
  }, []);

  const save = async () => {
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API_URL}/team/${editing.id}` : `${API_URL}/team`;
    const res = await fetch(url, { method, headers:{"Content-Type":"application/json"}, body: JSON.stringify({...form, id: editing?editing.id:Date.now()}) });
    if(res.ok){ setData(await res.json()); setShowForm(false); setEditing(null); setForm({}); onToast("Saved!","success"); }
    else onToast("Save failed","error");
  };

  const remove = async (item) => {
    const res = await fetch(`${API_URL}/team/${item.id}`, {method:"DELETE"});
    if(res.ok){ setData(await res.json()); setDeleteTarget(null); onToast("Deleted!","success"); }
  };

  const columns=[{key:"name",label:"Name"},{key:"role",label:"Role"},{key:"linkedin",label:"LinkedIn"}];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900">Team Members</h2>
        <button onClick={()=>{setEditing(null);setForm({name:"",nameAr:"",role:"",roleAr:"",photo:"",bio:"",bioAr:"",linkedin:"#"});setShowForm(true);}} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-xl text-sm font-semibold"><Plus size={16}/> Add Member</button>
      </div>
      {showForm && (
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">{editing?"Edit Member":"New Member"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[["name","Name (EN)"],["nameAr","Name (AR)"],["role","Role (EN)"],["roleAr","Role (AR)"],["photo","Photo URL"],["linkedin","LinkedIn URL"],["bio","Bio (EN)"],["bioAr","Bio (AR)"]].map(([k,l])=>(
              <div key={k} className={k==="bio"||k==="bioAr"?"sm:col-span-2":""}>
                <label className="text-xs text-gray-500 block mb-1">{l}</label>
                {k==="bio"||k==="bioAr" ? <textarea rows={2} value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"/> : <input value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30"/>}
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={save} className="flex items-center gap-2 bg-accent text-primary px-5 py-2 rounded-xl text-sm font-semibold"><Save size={14}/> Save</button>
            <button onClick={()=>{setShowForm(false);setEditing(null);setForm({});}} className="flex items-center gap-2 bg-gray-200 text-gray-700 px-5 py-2 rounded-xl text-sm font-semibold"><X size={14}/> Cancel</button>
          </div>
        </div>
      )}
      <DataTable items={data} columns={columns} onEdit={(item)=>{setEditing(item);setForm(item);setShowForm(true);}} onDelete={setDeleteTarget}/>
      <AnimatePresence>{deleteTarget&&<ConfirmModal message={`Delete "${deleteTarget.name}"?`} onConfirm={()=>remove(deleteTarget)} onCancel={()=>setDeleteTarget(null)}/>}</AnimatePresence>
    </div>
  );
};

// ─── Settings Section ─────────────────────────────────────────────────────
const SettingsSection = ({ onToast }) => {
  const [form, setForm] = useState({ company: {}, social: {} });

  useEffect(() => {
    fetch(`${API_URL}/settings`).then(r=>r.json()).then(setForm).catch(()=>{});
  }, []);

  const save = async () => {
    const res = await fetch(`${API_URL}/settings`, { method:"PUT", headers:{"Content-Type":"application/json"}, body: JSON.stringify(form) });
    if(res.ok) onToast("Settings saved!","success");
    else onToast("Save failed","error");
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-6">Company Settings</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-700 mb-4">Company Info</h3>
          <div className="space-y-3">
            {[["name","Company Name"],["tagline","Tagline (EN)"],["taglineAr","Tagline (AR)"],["email","Email"],["phone","Phone"],["address","Address (EN)"],["addressAr","Address (AR)"]].map(([k,l])=>(
              <div key={k}>
                <label className="text-xs text-gray-500 block mb-1">{l}</label>
                <input value={form.company?.[k]||""} onChange={e=>setForm(p=>({...p,company:{...p.company,[k]:e.target.value}}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 bg-white"/>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
          <h3 className="font-semibold text-gray-700 mb-4">Social Links</h3>
          <div className="space-y-3">
            {[["linkedin","LinkedIn"],["github","GitHub"],["twitter","Twitter"],["instagram","Instagram"]].map(([k,l])=>(
              <div key={k}>
                <label className="text-xs text-gray-500 block mb-1">{l}</label>
                <input value={form.social?.[k]||""} onChange={e=>setForm(p=>({...p,social:{...p.social,[k]:e.target.value}}))} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 bg-white"/>
              </div>
            ))}
          </div>
        </div>
      </div>
      <button onClick={save} className="mt-6 flex items-center gap-2 bg-accent text-primary px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-accent/90 transition-colors">
        <Save size={14}/> Save Settings
      </button>
    </div>
  );
};

// ─── Main Admin Component ─────────────────────────────────────────────────
const Admin = () => {
  const [loggedIn, setLoggedIn] = useState(() => !!sessionStorage.getItem("applumo_admin"));
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [activeTab, setActiveTab] = useState("projects");
  const [toast, setToast] = useState(null);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        sessionStorage.setItem("applumo_admin", "1");
        setLoggedIn(true);
        setLoginError("");
      } else {
        setLoginError("Invalid credentials. Please try again.");
      }
    } catch {
      setLoginError("Cannot connect to server. Make sure it is running.");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("applumo_admin");
    setLoggedIn(false);
  };

  const tabs = [
    { key: "projects", label: "Projects", icon: FolderOpen },
    { key: "jobs", label: "Jobs", icon: Briefcase },
    { key: "clients", label: "Clients", icon: Users },
    { key: "team", label: "Team", icon: UserCircle },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark to-primary flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-10"
        >
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-accent font-bold text-2xl">A</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-gray-400 text-sm mt-1">Sign in to manage content</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
              <input
                required
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  required
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {loginError && (
              <p className="text-red-500 text-sm bg-red-50 px-4 py-2.5 rounded-xl">{loginError}</p>
            )}
            <button
              type="submit"
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-xl hover:bg-primary/90 transition-colors"
            >
              Sign In
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AnimatePresence>
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 min-h-screen">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-accent font-bold text-sm">A</span>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">Applumo</p>
              <p className="text-gray-400 text-xs">Admin Panel</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest px-3 mb-3">
            Content
          </p>
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                activeTab === key
                  ? "bg-primary text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 min-h-screen overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <LayoutDashboard size={14} />
              <span>/</span>
              <span className="text-gray-700 font-medium capitalize">{activeTab}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <span className="text-accent font-bold text-xs">A</span>
              </div>
              <span className="text-sm font-medium text-gray-700">{username}</span>
            </div>
          </div>
        </div>

        <div className="p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === "projects" && <ProjectsSection onToast={showToast} />}
              {activeTab === "jobs" && <JobsSection onToast={showToast} />}
              {activeTab === "clients" && <ClientsSection onToast={showToast} />}
              {activeTab === "team" && <TeamSection onToast={showToast} />}
              {activeTab === "settings" && <SettingsSection onToast={showToast} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Admin;
